import { useState, type CSSProperties } from 'react'
import {
  AlertIcon,
  ChevronIcon,
  DataSurface,
  InfoIcon,
  Note,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getAttendanceMonth,
  type MonthAttendance,
  type MonthChildRow,
  type MonthDay,
} from '../../../shared/api/attendance'
import type { StaffAuth } from '../../../shared/api/teaching'
import { TEACHER_COPY, describeError, type Lang } from './copy'
import {
  dayNumber,
  intensity,
  monthLabel,
  monthOf,
  nextMonth,
  previousMonth,
  rosterOn,
  weeksOf,
  type DayRoster,
} from './attendanceMonth'

/**
 * A month of the register, as a calendar and a per-child tally.
 *
 * 🔴 THE SCREEN ABOVE THIS ONE IS TODAY AND ONLY TODAY. `AttendanceScreen`
 * marks and saves one day — the controller says *"yesterday is history and is
 * not editable here"* — so a teacher asked *"how has Meera's attendance been?"*
 * at a parents' evening had nothing to look at. This answers that and changes
 * nothing about the write.
 *
 * 🔴 A BLANK DAY IS BLANK, NOT GREEN, AND THIS IS THE ONE RULE THE WHOLE PANEL
 * IS BUILT AROUND. `Absence` stores absences only: a day every child was
 * present writes no rows, so it reaches the browser identical to a Sunday, a
 * holiday, or a day nobody opened the register. Painting those days as "all
 * present" would invent a register that was never taken — `GrowthChart`'s
 * *"worse than an empty card, because it is convincing"*. The API says
 * `recorded: false` and this draws nothing.
 *
 * ⚠️ READ ONLY, DELIBERATELY. There is no month-shaped write and there must not
 * be: `saveAttendance` takes its day from the SERVER precisely so one request
 * cannot rewrite a week and message parents about days that have passed.
 * Nothing here is clickable-to-edit.
 */
export interface AttendanceMonthPanelProps {
  classId: string
  lang?: Lang
  auth?: StaffAuth
}

export function AttendanceMonthPanel({
  classId,
  lang = 'en',
  auth,
}: AttendanceMonthPanelProps) {
  const t = TEACHER_COPY[lang]
  /*
   * ⚠️ THE MONTH IS THIS COMPONENT'S STATE AND PART OF THE RESOURCE KEY, so
   * stepping to another month issues its own request and gets its own four
   * states — a spinner over stale numbers would show September's tally under
   * August's heading.
   */
  const [month, setMonth] = useState(() => monthOf(new Date()))
  const thisMonth = monthOf(new Date())

  const resource = useResource<MonthAttendance>(
    (signal) => getAttendanceMonth(classId, month, { ...auth, signal }),
    ['attendance-month', classId, month],
  )

  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-card sm:p-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-dash text-dash-md font-extrabold text-ink">
          {monthLabel(month)}
        </h2>

        {/*
          ⚠️ THREE CONTROLS, AND THE MIDDLE ONE IS NOT DECORATION. Stepping back
          six months and then wanting to get home is otherwise six clicks; a
          teacher who has wandered needs one way back to the month she is
          actually teaching.
        */}
        <div className="flex items-center gap-1.5">
          <MonthStep
            label={t.monthPrev}
            onClick={() => { setMonth(previousMonth(month)); }}
            back
          />
          <button
            type="button"
            disabled={month === thisMonth}
            onClick={() => { setMonth(thisMonth); }}
            className="rounded-full border border-line bg-surface px-3 py-1.5 font-dash text-dash-xs font-bold text-ink transition-colors hover:bg-canvas disabled:cursor-default disabled:opacity-40 cursor-pointer"
          >
            {t.monthToday}
          </button>
          <MonthStep
            label={t.monthNext}
            onClick={() => { setMonth(nextMonth(month)); }}
          />
        </div>
      </div>

      <p className="mb-4 max-w-prose font-dash text-dash-xs text-muted">
        {t.monthHint}
      </p>

      <DataSurface
        state={resource.state}
        skeletonRows={6}
        errorMessage={describeError(resource.error, lang)}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : (
          <MonthGrid data={resource.data} lang={lang} />
        )}
      </DataSurface>
    </div>
  )
}

function MonthStep({
  label,
  onClick,
  back = false,
}: {
  label: string
  onClick: () => void
  back?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface text-ink transition-colors hover:bg-canvas cursor-pointer"
    >
      <ChevronIcon
        size={16}
        strokeWidth={2.6}
        aria-hidden="true"
        className={back ? 'rotate-180' : undefined}
      />
    </button>
  )
}

function MonthGrid({ data, lang }: { data: MonthAttendance; lang: Lang }) {
  const t = TEACHER_COPY[lang]
  const weeks = weeksOf(data.days)
  const anyMarks = data.days.some((d) => d.recorded)
  const withAbsences = data.children.filter((c) => c.absentDays.length > 0)

  const [openDay, setOpenDay] = useState<string | null>(null)
  /*
   * 🔴 THE OPEN DAY IS RESOLVED AGAINST THIS MONTH'S DAYS, NOT TRUSTED AS STATE.
   * Stepping to another month leaves `openDay` holding a date that month does
   * not contain, and `find` answers `null` for it — so the panel closes itself
   * without an effect, a cleanup, or a stale August date sitting under a
   * September heading. State that cannot be wrong beats state that is corrected.
   */
  const open = data.days.find((d) => d.day === openDay) ?? null
  const roster = rosterOn(data.children, open)

  return (
    <div className="flex flex-col gap-5">
      {/* ── the calendar ── */}
      <div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {t.monthWeekdays.map((wd) => (
            <span
              key={wd}
              className="pb-1 text-center font-dash text-dash-xs font-bold text-muted"
            >
              {wd}
            </span>
          ))}

          {weeks.flat().map((cell, i) =>
            cell === null ? (
              /* ⚠️ An empty cell, not a neighbouring month's date — see
                 `weeksOf`. Nothing here can be misread as a day. */
              <span key={`blank-${String(i)}`} aria-hidden="true" />
            ) : (
              <DayCell
                key={cell.day}
                day={cell}
                index={i % 7}
                childCount={data.childCount}
                open={cell.day === open?.day}
                onPick={() => {
                  setOpenDay(cell.day === openDay ? null : cell.day)
                }}
                lang={lang}
              />
            ),
          )}
        </div>

        {!anyMarks && (
          <p className="mt-3 font-dash text-dash-xs text-muted">{t.monthNoMarks}</p>
        )}
        {anyMarks && open === null && (
          <p className="mt-3 font-dash text-dash-xs text-muted">{t.monthPickDay}</p>
        )}
      </div>

      {/*
        🔴 THE DAY OPENS UNDER THE GRID, NOT IN A DIALOG. A modal would cover the
        calendar the reader is comparing against — and this panel's whole use is
        "the 4th was bad, what about the 11th", which is two clicks on a grid
        that has to stay on screen.
      */}
      {open !== null && (
        <DayDetail
          day={open}
          roster={roster}
          childCount={data.childCount}
          onClose={() => {
            setOpenDay(null)
          }}
          lang={lang}
        />
      )}

      {/* ── the per-child tally ── */}
      <div className="border-t border-line pt-4">
        <h3 className="mb-2 font-dash text-dash-sm font-extrabold text-ink">
          {t.monthPerChild}
        </h3>
        {withAbsences.length === 0 ? (
          <Note tone="neutral" icon={<InfoIcon size={16} />}>
            {t.monthNoMarks}
          </Note>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {withAbsences.map((child) => (
              <li
                key={child.playerId}
                className="flex items-center justify-between gap-4 rounded-md px-2 py-1.5 odd:bg-canvas"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {child.rollNumber && (
                    <span className="tabular shrink-0 font-dash text-dash-xs font-bold text-muted">
                      {child.rollNumber}
                    </span>
                  )}
                  <span className="truncate font-dash text-dash-sm font-bold text-ink">
                    {child.name}
                  </span>
                </span>
                <span className="tabular shrink-0 font-dash text-dash-xs font-bold text-muted">
                  {t.monthDaysAbsent(child.absentDays.length)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * One day.
 *
 * 🔴 THE NUMBER IS ALWAYS PRINTED WHEN THERE ARE ABSENCES — the tint is the
 * second carrier, never the only one (§12). A heat map alone would be unreadable
 * for a reader who cannot separate its shades, and this one is read to decide
 * whether to telephone a family.
 */
function DayCell({
  day,
  index,
  childCount,
  open,
  onPick,
  lang,
}: {
  day: MonthDay
  /** Column in its row — drives the stagger, so a week arrives as a week. */
  index: number
  childCount: number
  open: boolean
  onPick: () => void
  lang: Lang
}) {
  const t = TEACHER_COPY[lang]
  const heat = intensity(day, childCount)
  const label = new Date(`${day.day}T00:00:00.000Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })

  /*
   * 🔴 `play-day` AND `play-day-on` ARE THE PRODUCT'S OWN, NOT NEW TAILWIND
   * HOVER CLASSES. `play.css` gives the hover its scale and shadow with the
   * house easing, gives a marked day a staggered arrival keyed off `--rise-i`,
   * and — the part that matters — lists both in the `prefers-reduced-motion`
   * block. A hand-rolled `hover:scale-105` here would animate for a reader who
   * has asked the whole operating system not to, and nothing would catch it.
   *
   * ⚠️ `play-day-on` ONLY FOR A MARKED DAY, which is that class's own rule:
   * *"an empty square that animated in would draw the eye to a day on which
   * nothing happened — which on this screen is most of them."*
   *
   * ⚠️ THE OPEN SQUARE IS RINGED, NOT RECOLOURED — the parent tier's calendar
   * gives the reason: the fill IS the day's state, so marking the selection with
   * a colour would make one square lie about a class's attendance for as long as
   * the panel is open. A ring sits outside the fill and says only "this one".
   */
  const shell = `play-day flex aspect-square w-full cursor-pointer flex-col items-center justify-center rounded-md border font-dash ${
    day.recorded ? 'play-day-on' : ''
  }${open ? ' outline-2 outline-offset-2 outline-ink' : ''}`

  return (
    <button
      type="button"
      onClick={onPick}
      /*
       * 🔴 A REAL `<button>`, AND IT KEEPS ITS ACCESSIBLE NAME EVEN WHEN THE
       * SQUARE SAYS NOTHING. A silent blank day is right for a static grid and
       * wrong for a control: one with no name is one a screen reader cannot
       * offer. `aria-pressed` says which day the panel below is about.
       */
      aria-pressed={open}
      aria-label={
        day.recorded
          ? t.monthDayLabel(label, day.absent)
          : t.monthDayLabelBlank(label)
      }
      /* ⚠️ THE TITLE IS THE SENTENCE, not the raw count — a tooltip reading "3"
         answers nothing on its own. */
      title={
        day.recorded ? t.monthAbsentOn(day.absent, label) : t.monthNothingOn(label)
      }
      className={shell}
      style={
        {
          '--rise-i': index,
          /*
           * ⚠️ THE TINT IS `coral` — §12 reserves it for what needs attention, and
           * an absence is the one thing on this grid a teacher may have to act on.
           * A day with nothing recorded gets no fill at all, which is what makes
           * "blank" visibly different from "everyone was here".
           */
          background: day.recorded
            ? `color-mix(in srgb, var(--color-coral) ${String(Math.round(12 + heat * 58))}%, var(--color-surface))`
            : 'var(--color-surface)',
          borderColor: day.recorded
            ? 'color-mix(in srgb, var(--color-coral) 40%, var(--color-line))'
            : 'var(--color-line)',
        } as CSSProperties
      }
    >
      <span
        className={`tabular text-dash-xs ${
          day.recorded ? 'font-extrabold text-ink' : 'font-bold text-muted'
        }`}
      >
        {dayNumber(day.day)}
      </span>
      {day.recorded && (
        <span className="tabular flex items-center gap-0.5 text-[10px] font-bold text-ink">
          <AlertIcon size={10} aria-hidden="true" />
          {day.absent}
        </span>
      )}
    </button>
  )
}

/** `2026-08-04` → `Tuesday, 4 August`, in the reader's locale, read as UTC. */
function longDay(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })
}

/**
 * One day, opened.
 *
 * 🔴 A DAY WITH NO MARKS GETS THE SENTENCE, NOT TWO EMPTY LISTS. `rosterOn`
 * answers `null` for it precisely so this cannot render "24 present, 0 absent"
 * about a Sunday — the convincing invention the whole panel is shaped to refuse.
 */
function DayDetail({
  day,
  roster,
  childCount,
  onClose,
  lang,
}: {
  day: MonthDay
  roster: DayRoster | null
  childCount: number
  onClose: () => void
  lang: Lang
}) {
  const t = TEACHER_COPY[lang]

  return (
    <div className="rounded-lg border border-line bg-canvas p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-dash text-dash-sm font-extrabold text-ink">
          {longDay(day.day)}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer rounded-full border border-line bg-surface px-3 py-1 font-dash text-dash-xs font-bold text-ink transition-colors hover:bg-surface"
        >
          {t.monthClose}
        </button>
      </div>

      {roster === null ? (
        <Note tone="neutral" icon={<InfoIcon size={16} />}>
          {t.monthDayNothing}
        </Note>
      ) : (
        <>
          {/* ⚠️ PRESENT FIRST, ABSENT SECOND. The register is read to find who is
              missing, and putting the short list where the eye lands last is how
              a teacher scrolls past the name she opened the day for. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <NameList
              title={t.monthDayPresent}
              count={t.monthDayCount(roster.present.length, childCount)}
              rows={roster.present}
              tone="var(--color-leaf)"
            />
            <NameList
              title={t.monthDayAbsent}
              count={t.monthDayCount(roster.absent.length, childCount)}
              rows={roster.absent}
              tone="var(--color-coral)"
            />
          </div>

          {/*
            🔴 THE CAVEAT SHIPS WITH THE NUMBERS, never behind a "more info".
            OD-39/OD-72 applied to a roster: the present column is an INFERENCE
            from "not marked absent", and a reader who cannot see that will read
            it as a register that was taken.
          */}
          <p className="mt-3 max-w-prose font-dash text-dash-xs text-muted">
            {t.monthPresentBasis}
          </p>
        </>
      )}
    </div>
  )
}

function NameList({
  title,
  count,
  rows,
  tone,
}: {
  title: string
  count: string
  rows: readonly MonthChildRow[]
  tone: string
}) {
  return (
    <div className="rounded-md border border-line bg-surface p-2.5">
      <p className="mb-2 flex items-center gap-2 font-dash text-dash-xs font-bold text-ink">
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ background: tone }}
        />
        {title}
        <span className="tabular ml-auto font-bold text-muted">{count}</span>
      </p>
      {rows.length === 0 ? (
        <p className="font-dash text-dash-xs text-muted">—</p>
      ) : (
        /* ⚠️ BOUNDED — a class of thirty would otherwise push the calendar off
           the screen the moment a day is opened, which is the one thing this
           panel is compared against. */
        <ul className="flex max-h-[13rem] flex-col gap-1 overflow-y-auto overscroll-contain">
          {rows.map((child) => (
            <li
              key={child.playerId}
              className="flex min-w-0 items-center gap-2 font-dash text-dash-xs"
            >
              {child.rollNumber && (
                <span className="tabular shrink-0 font-bold text-muted">
                  {child.rollNumber}
                </span>
              )}
              <span className="truncate font-bold text-ink">{child.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

